import { c as _c } from "react/compiler-runtime";
import { cva, type VariantProps } from "class-variance-authority";
import { Slot } from "radix-ui";
import type * as React from "react";
import { cn } from "~/lib/utils";
const buttonVariants = cva([
// Layout
"inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap",
// Cursor
"cursor-pointer",
// Typography — UI font, semibold
"font-ui font-semibold no-underline select-none",
// Motion — snap easing for all interactive state changes
"[transition:all_var(--dur-fast)_var(--ease-snap)]",
// Focus ring uses brand blue
"focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--blue)] focus-visible:ring-offset-2",
// Disabled & loading states
"disabled:pointer-events-none disabled:opacity-40", "aria-busy:cursor-wait",
// Icon sizing inside buttons
"[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4"].join(" "), {
  variants: {
    variant: {
      // ── Primary — sole CTA, full pill, electric blue ──────────────────────
      primary: ["rounded-[var(--r-full)] border-none", "bg-[var(--blue)] text-white", "hover:-translate-y-px hover:bg-[var(--blue-dark)] hover:shadow-md", "active:translate-y-0 active:shadow-sm active:bg-[var(--blue-dark)]"].join(" "),
      // ── Ghost — secondary nav actions, no chrome ──────────────────────────
      ghost: ["rounded-[var(--r-full)] border-none", "bg-transparent text-[var(--ink-80)]", "hover:bg-[var(--ink-10)] hover:text-[var(--ink)]", "active:bg-[var(--ink-20)]"].join(" "),
      // ── Outline — pairs with Primary in dual-action zones ─────────────────
      outline: ["rounded-[var(--r-lg)]", "border border-[var(--blue)] bg-transparent text-[var(--blue)]", "hover:bg-[var(--blue-pale)] hover:border-[var(--blue-dark)] hover:text-[var(--blue-dark)]", "active:bg-[var(--blue-pale)]"].join(" "),
      // ── Danger — destructive, rare ────────────────────────────────────────
      danger: ["rounded-[var(--r-lg)]", "border border-transparent bg-[var(--red-bg)] text-[var(--red)]", "hover:border-[var(--red)]", "active:opacity-90"].join(" "),
      // ── Backward-compat aliases ───────────────────────────────────────────
      // `destructive` → same as danger
      destructive: ["rounded-[var(--r-lg)]", "border border-transparent bg-[var(--red-bg)] text-[var(--red)]", "hover:border-[var(--red)]"].join(" "),
      // `secondary` → neutral bordered, kept for existing usage
      secondary: ["rounded-[var(--r-full)]", "border border-[var(--ink-20)] bg-transparent text-[var(--ink-60)]", "hover:bg-[var(--ink-10)] hover:text-[var(--ink)] hover:border-[var(--ink-40)]", "active:bg-[var(--ink-20)]"].join(" "),
      // Contextual — dark-surface payout panel
      "payout-primary": ["rounded-[var(--r-full)]", "bg-[var(--blue)] text-white", "hover:bg-[var(--blue-dark)] hover:-translate-y-px", "active:translate-y-0"].join(" "),
      "payout-ghost": ["rounded-[var(--r-full)]", "border border-[rgba(255,255,255,0.20)] bg-[rgba(255,255,255,0.12)] text-white", "hover:bg-[rgba(255,255,255,0.20)] hover:border-[rgba(255,255,255,0.30)]"].join(" "),
      // Contextual — social / OAuth buttons
      social: ["rounded-[var(--r-full)]", "border border-[var(--ink-20)] bg-white text-[var(--ink)]", "hover:bg-[var(--ink-05)] hover:border-[var(--ink-60)]", "active:bg-[var(--ink-10)]"].join(" ")
    },
    size: {
      // xs — chips, tight inline actions
      xs: "h-7 px-3 text-xs",
      // sm — default; nav actions, secondary CTAs
      sm: "h-9 px-4 text-[13px]",
      // md — primary CTA at standard density
      md: "h-10 px-5 text-sm",
      // lg — hero / page-level CTA
      lg: "h-12 px-7 text-base",
      // icon — square icon-only button
      icon: "size-10 rounded-[var(--r-md)] p-0"
    }
  },
  defaultVariants: {
    variant: "primary",
    size: "md"
  }
});
type ButtonProps = React.ComponentProps<"button"> & VariantProps<typeof buttonVariants> & {
  asChild?: boolean;
};
function Button(t0) {
  const $ = _c(14);
  let className;
  let props;
  let size;
  let t1;
  let variant;
  if ($[0] !== t0) {
    ({
      className,
      variant,
      size,
      asChild: t1,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = size;
    $[4] = t1;
    $[5] = variant;
  } else {
    className = $[1];
    props = $[2];
    size = $[3];
    t1 = $[4];
    variant = $[5];
  }
  const asChild = t1 === undefined ? false : t1;
  const Comp = asChild ? Slot.Root : "button";
  let t2;
  if ($[6] !== className || $[7] !== size || $[8] !== variant) {
    t2 = cn(buttonVariants({
      variant,
      size,
      className
    }));
    $[6] = className;
    $[7] = size;
    $[8] = variant;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  let t3;
  if ($[10] !== Comp || $[11] !== props || $[12] !== t2) {
    t3 = <Comp data-slot="button" className={t2} {...props} />;
    $[10] = Comp;
    $[11] = props;
    $[12] = t2;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  return t3;
}
export { Button, buttonVariants };